/**
 * Contains the component for message bundle access.
 *
 * File: \src\I18nStore.ts
 * Created Date: Sunday, March 4th 2018, 16:55:14
 * Author: Sascha Herrmann
 * -----
 * Last Modified: May 19th 2025, 09:55:30
 * Modified By: Herrmann Sascha (LDC)
 * -----
 * Copyright (c) 2017-2018 Liebherr-Hausgeräte GmbH
 */

import { Composer, NamedValue } from 'vue-i18n';
import { Ref, nextTick } from 'vue';

import { Settings } from 'luxon';

import LocalStorage from '@/components/general/storage/LocalStorage';
import LocalStorageKeys from '@/components/general/storage/LocalStorageKeys';

import { UserSettingsLanguageEnum } from '@/api/models';

import i18n from '@/main';
import { defineStore } from 'pinia';
import { CubeLogger } from '@/Logger';

export interface Language {
  name: string;
  code: string;
  language: string;
  country: string;
}

/**
 * Defines the store's state.
 *
 * @interface ConfigState
 */
interface I18nState {
  loadedLanguage: Language;
  fallbackLanguage: Language;
  availableLanguages: Language[];
}

const localStorageKey: string = 'language';

const languages: Language[] = [
  { name: 'English', code: 'en-US', language: 'en', country: 'US' },
  { name: 'Deutsch', code: 'de-DE', language: 'de', country: 'DE' }
];

/**
 * This class switches the language in the UI for us.
 * It extends VueI18n for that purpose. Message bundles are
 * assumed to be in the same folder, named '<purpose>_<locale>'.json.
 *
 * @export
 * @class I18nSwitcher
 * @extends {Vue}
 */

export const useI18nStore = defineStore('i18nStore', {
  // The state. Language names are i18n'ed and hardcoded by design!
  state: (): I18nState => ({
    availableLanguages: languages,
    loadedLanguage: languages[0],
    fallbackLanguage: languages[0]
  }),

  getters: {
    /**
     * Getter for the available languages.
     *
     * @return {*}  {Language[]} All available languages.
     */
    getAvailableLanguages(): Language[] {
      return this.availableLanguages;
    },

    /**
     * Getter for the loaded language.
     *
     * @return {*}  {Language} The language.
     */
    getLoadedLanguage(): Language {
      return this.loadedLanguage;
    },

    /**
     * Getter for the loaded language.
     *
     * @return {*}  {string} the IANA code.
     */
    getLoadedLanguageCode(): string {
      return this.loadedLanguage.code;
    },

    /**
     * Getter for the name of the loaded language.
     *
     * @return {*}  {string} The language name, e.g. Español
     */
    getLoadedLanguageName(): string {
      return this.loadedLanguage.name;
    },

    /**
     * Getter for the loaded language.
     *
     * @return {*}  {string} e.g. "en", "de", "fr" or "es"
     */
    getLoadedLanguageLanguage(): string {
      return this.loadedLanguage.language;
    },

    /**
     * CHecks whether a language wil the code is loaded currently.
     *
     * @return {*}  {(code: string) => boolean} Function to check.
     */
    isLanguageLoaded(): (code: string) => boolean {
      return (code: string): boolean => {
        return this.loadedLanguage.code === code;
      };
    },

    /**
     * Getting the checked language from the browser.
     *
     * @return {*}  {Language} The found language with en_US as fallback.
     */
    getCheckedBrowserLanguage(): Language {
      // Try to match navigator.language to our language code ("en-US", "en-GB", ...) exactly first:
      let foundLanguage: Language = this.availableLanguages.find(
        availLanguage => navigator.language === availLanguage.code
      );

      // If not found, look for the language by language ("en", "de", ...):
      if (!foundLanguage) {
        foundLanguage = this.availableLanguages.find(availLanguage =>
          navigator.language.startsWith(availLanguage.language)
        );
      }

      if (!foundLanguage) {
        // Default: en-US
        foundLanguage = this.fallbackLanguage;
      }

      return foundLanguage;
    },

    /**
     * Quick helper. There is no easy way to reverse lookup in a string based enum in typescript.
     * Kind of sucks.
     *
     * @return {*}  {(value: string) => UserSettingsLanguageEnum}
     */
    getUserSettingsLanguage(): (value: string) => UserSettingsLanguageEnum {
      return (value: string): UserSettingsLanguageEnum => {
        switch (value) {
          // Legacy
          case 'en': {
            return UserSettingsLanguageEnum.EN_US;
          }

          // Legacy
          case 'de': {
            return UserSettingsLanguageEnum.DE_DE;
          }

          case 'en-US': {
            return UserSettingsLanguageEnum.EN_US;
          }

          case 'de-DE': {
            return UserSettingsLanguageEnum.DE_DE;
          }

          default: {
            return UserSettingsLanguageEnum.EN_US;
          }
        }
      };
    },

    /**
     * Quick helper. There is no easy way to reverse lookup in a string based enum in typescript.
     * Kind of sucks.
     *
     * @return {*}  {(languageValue: UserSettingsLanguageEnum) => string}
     */
    getLocaleLanguage(): (languageValue: UserSettingsLanguageEnum) => string {
      return (languageValue: UserSettingsLanguageEnum): string => {
        switch (languageValue) {
          case UserSettingsLanguageEnum.EN_US: {
            return 'en-US';
          }

          case UserSettingsLanguageEnum.DE_DE: {
            return 'de-DE';
          }

          default: {
            return 'en-US';
          }
        }
      };
    }
  },

  actions: {
    /**
     * Access to the standard t method. You can pass a NamedValue instance for named interpolation.
     * This is needed because the original useI18n can only be used in Vue components.
     *
     * @example
     * "t('just.key')"
     * "t('just.key.interpolated', { toreplace: 'sometext' })" with "'just.key.interpolated' = 'Test {toreplace}'"
     *
     * @param {string} key The key to access.
     * @param {NamedValue} [namedValue] The named values to replace/interpolate
     * @return {*}  {string} The string out of the bundle.
     */
    t(key: string, namedValue?: NamedValue): string {
      const composer: Composer = i18n as Composer;
      return composer.t(key, namedValue).toString();
    },

    /**
     * Access to the standard tc method. You can a value to choose the interpolation.
     * This is needed because the original useI18n can only be used in Vue components.
     *
     * @example
     * tc('just.key', 0) with "'just.key' = 'None|One|Multiple'" (result: "None")
     * tc('just.key', 1) with "'just.key' = 'None|One|Multiple'" (result: "One")
     * tc('just.key', 5) with "'just.key' = 'None|One|Multiple'" (result: "Multiple")
     *
     * @return {*}  {string} The string out of the bundle.
     */
    tc(key: string, plural: number): string {
      const composer: Composer = i18n as Composer;
      return composer.t(key, plural).toString();
    },

    /**
     * Inits the store. Uses en-US as the initial default language. This will be later changed with the
     * language loaded by the ConfigStore.
     */
    init(): void {
      const browserLanguage: Language = this.getCheckedBrowserLanguage;
      const language: string = LocalStorage.getItem(LocalStorageKeys.LOCAL_STORAGE_LANGUAGE_KEY, browserLanguage.code);

      this.loadLanguageString(language);
    },

    /**
     * Sets the language by changing the locale in VueI18n. Also changes
     * the lang attribute of the document.
     *
     * @param {string} lang The new language.
     */
    setI18nLanguage(lang: string): void {
      (i18n.locale as Ref<string>).value = lang;

      document.querySelector('html')?.setAttribute('lang', lang);

      Settings.defaultLocale = lang;
    },

    /**
     * Utility method that allows loading a language by string. This lookup is strict.
     *
     * @param {string} language The ISO code of the language.
     */
    loadLanguageString(language: string): void {
      // Legacy fallback
      if (language === 'de' || language === 'deDE') {
        language = 'de-DE';
      }

      // Legacy fallback
      if (language === 'en' || language === 'enUS') {
        language = 'en-US';
      }

      let foundLanguage: Language = this.availableLanguages.find(available => available.code === language);

      // Even more fallback
      if (!foundLanguage) {
        foundLanguage = this.fallbackLanguage;
      }

      this.loadLanguageAsync(foundLanguage);
    },

    /**
     * Loads the language asynchronously.
     *
     * @param {Language} language The language to load.
     * @return {*}  {Promise<void>}
     */
    async loadLanguageAsync(language: Language): Promise<void> {
      const logger = CubeLogger.$log.prefix('LanguageSetup::loadLanguageAsync');

      LocalStorage.setItem(LocalStorageKeys.LOCAL_STORAGE_LANGUAGE_KEY, language.code);
      this.setI18nLanguage(language.code);

      try {
        const [bizMsgs, generalMsgs] = await Promise.all([
          import(`./lang/biz/${language.code}.json`),
          import(`./lang/general/${language.code}.json`)
        ]);

        // set locale and locale message
        i18n.setLocaleMessage(language.code, bizMsgs.default);
        i18n.mergeLocaleMessage(language.code, generalMsgs.default);
        (i18n.locale as Ref<string>).value = language.code;

        this.loadedLanguage = language;
      } catch (error) {
        logger.debug('Could not load translation messages...', { error: error });
      }

      return nextTick();
    }
  }
});
